import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Pagination,
  Avatar
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import axiosInstance from "API/axiosInstance";
import { decryptText } from "config/encryption";
import MDButton from "components/MDButton";
import Autocomplete from '@mui/material/Autocomplete';
import StudentFullProfile from "layouts/profile/components/StudentFullProfile";
import { API_ENDPOINTS, getUploadUrl } from "config/api";

// Get staffId and token from localStorage
const getAdminSession = () => {
  try {
    const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      const decrypted = decryptText(session);
      if (decrypted) {
        const parsed = JSON.parse(decrypted);
        return {
          staffId: parsed.StaffId || parsed.staffId || parsed.StaffCode || parsed.staffCode || null,
          token: parsed.Token || parsed.token || null
        };
      }
    }
  } catch (error) {
    console.error("Error extracting admin session:", error);
  }
  return { staffId: null, token: null };
};

const { staffId, token } = getAdminSession();

const AdminAlumniRightsManager = ({ onClose }) => {
  const [chapters, setChapters] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [editRoleDialog, setEditRoleDialog] = useState({ open: false, member: null });
  const [roleIdToUpdate, setRoleIdToUpdate] = useState("");
  const [addRoleDialog, setAddRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState({ roleName: "", description: "" });
  const [memberPage, setMemberPage] = useState(1);
  const membersPerPage = 12;

  useEffect(() => {
    const fetchChaptersAndRoles = async () => {
      try {
        // Verify token is available before making request
        const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
        if (!session) {
          console.error("[AdminAlumniRightsManager] No admin session found in localStorage");
          setSnackbar({ open: true, message: "No admin session found. Please log in again.", severity: "error" });
          return;
        }
        
        const res = await axiosInstance.get(API_ENDPOINTS.ADMIN.ADMIN_CHAPTER_DROPDOWN_DETAILS);
        setChapters(res.data.chapters || []);
        setRoles(res.data.roles || []);
      } catch (error) {
        console.error("Error fetching chapters and roles:", error);
        setSnackbar({ 
          open: true, 
          message: error.response?.data?.errorMessage || "Failed to fetch chapters and roles", 
          severity: "error" 
        });
      }
    };
    fetchChaptersAndRoles();
  }, []);

  const fetchMembers = async (chapterId) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.ADMIN.FETCH_CHAPTER_MEMBER_ROLES(chapterId));
      setMembers(res.data);
    } catch (error) {
      console.error("Error fetching members:", error);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChapterClick = (chapter) => {
    setSelectedChapter(chapter);
    fetchMembers(chapter.id);
  };

  const handleEditRole = (member) => {
    setEditRoleDialog({ open: true, member });
    setRoleIdToUpdate(member.roleId);
  };

  const handleUpdateRole = async () => {
    try {
      await axiosInstance.post(API_ENDPOINTS.ADMIN.UPDATE_CHAPTER_MEMBER_ROLE, {
        chapterMemberId: editRoleDialog.member.chapterMemberId,
        roleId: roleIdToUpdate
      });
      setSnackbar({ open: true, message: "Role updated successfully.", severity: "success" });
      setEditRoleDialog({ open: false, member: null });
      fetchMembers(selectedChapter.id);
    } catch (error) {
      console.error("Error updating role:", error);
      setSnackbar({ open: true, message: "Failed to update role.", severity: "error" });
    }
  };

  const handleAddRole = async () => {
    try {
      await axiosInstance.post(API_ENDPOINTS.ADMIN.INSERT_CHAPTER_ROLE, newRole);
      setSnackbar({ open: true, message: "Role added.", severity: "success" });
      setAddRoleDialog(false);
      setNewRole({ roleName: "", description: "" });
      // Refresh roles
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.ADMIN.ADMIN_CHAPTER_DROPDOWN_DETAILS);
        setRoles(res.data.roles || []);
      } catch (error) {
        console.error("Error refreshing roles:", error);
      }
    } catch (error) {
      console.error("Error adding role:", error);
      setSnackbar({ open: true, message: "Failed to add role.", severity: "error" });
    }
  };

  // Reset member page if members change
  useEffect(() => {
    const totalPages = Math.ceil(members.length / membersPerPage);
    if (memberPage > totalPages) {
      setMemberPage(totalPages || 1);
    } else if (memberPage < 1) {
      setMemberPage(1);
    }
  }, [members, memberPage]);

  return (
    <Box sx={{ width: 400, maxWidth: '100vw', p: 3, position: 'relative', bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Tooltip title="Close">
          <IconButton onClick={onClose} color="error">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="h5" fontWeight="bold" mb={2}>Alumni Rights</Typography>
      <MDButton color="info" variant="gradient" startIcon={<AddIcon />} onClick={() => setAddRoleDialog(true)} sx={{ mb: 2 }}>
        Add Role
      </MDButton>
      {!selectedChapter ? (
        <>
          <Box mb={3}>
            <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>
              Select a Chapter to Manage Rights
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click a chapter below to view and manage its members and roles.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {chapters.map((chapter) => (
              <Grid item xs={12} key={chapter.id}>
                <Card
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    boxShadow: 2,
                    bgcolor: 'linear-gradient(90deg, #e3f2fd 0%, #fff 100%)',
                    minHeight: 56,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderLeft: '4px solid #1976d2',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-1px) scale(1.01)',
                      bgcolor: '#e3f2fd',
                    },
                  }}
                  onClick={() => handleChapterClick(chapter)}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="center" sx={{ bgcolor: '#1976d2', borderRadius: '50%', width: 32, height: 32, mr: 1.5 }}>
                      <GroupIcon sx={{ color: '#fff', fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="bold" color="info.main" sx={{ fontSize: 15 }}>
                        {chapter.txt}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                        Short: {chapter.shortName || chapter.chapterShortName || ''}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <>
          <MDButton color="info" variant="outlined" onClick={() => setSelectedChapter(null)} sx={{ mb: 2 }}>Back to Chapters</MDButton>
          <Typography variant="h6" mb={2}>{selectedChapter.txt} - Members</Typography>
          {loading ? <CircularProgress /> : (
            <>
              <Grid container spacing={2}>
                {members.slice((memberPage-1)*membersPerPage, memberPage*membersPerPage).map((member) => (
                  <Grid item xs={12} sm={4} md={4} key={member.chapterMemberId}>
                    <Card sx={{ borderRadius: 2, boxShadow: 1, bgcolor: '#fff', minHeight: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 1 }}>
                      <Avatar
                        sx={{ width: 36, height: 36, mb: 1, bgcolor: 'info.main' }}
                        src={member.img ? getUploadUrl(member.img) : undefined}
                      >
                        {!member.img && <PersonIcon sx={{ color: '#fff' }} />}
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium" noWrap sx={{ fontSize: 13, mb: 0.5, textAlign: 'center', maxWidth: 100 }}>{member.fullName}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, mb: 0.5 }}>{member.roleName}</Typography>
                      <Tooltip title="Edit Role">
                        <IconButton size="small" color="info" onClick={() => handleEditRole(member)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={Math.ceil(members.length / membersPerPage)}
                  page={memberPage}
                  onChange={(_, value) => setMemberPage(value)}
                  color="info"
                  shape="rounded"
                />
              </Box>
            </>
          )}
        </>
      )}
      {/* Edit Role Dialog */}
      <Dialog open={editRoleDialog.open} onClose={() => setEditRoleDialog({ open: false, member: null })}>
        <DialogTitle>Edit Member Role</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={roles}
            getOptionLabel={(option) => option.txt || ''}
            value={roles.find((r) => r.id === roleIdToUpdate) || null}
            onChange={(_, value) => setRoleIdToUpdate(value ? value.id : '')}
            renderInput={(params) => (
              <TextField {...params} label="Role" margin="dense" fullWidth />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setEditRoleDialog({ open: false, member: null })} color="info" variant="outlined">Cancel</MDButton>
          <MDButton onClick={handleUpdateRole} color="info" variant="gradient" startIcon={<SaveIcon />}>Update</MDButton>
        </DialogActions>
      </Dialog>
      {/* Add Role Dialog */}
      <Dialog open={addRoleDialog} onClose={() => setAddRoleDialog(false)}>
        <DialogTitle>Add New Role</DialogTitle>
        <DialogContent>
          <TextField
            label="Role Name"
            value={newRole.roleName}
            onChange={(e) => setNewRole((prev) => ({ ...prev, roleName: e.target.value }))}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Description"
            value={newRole.description}
            onChange={(e) => setNewRole((prev) => ({ ...prev, description: e.target.value }))}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setAddRoleDialog(false)} color="info" variant="outlined">Cancel</MDButton>
          <MDButton onClick={handleAddRole} color="info" variant="gradient" startIcon={<AddIcon />}>Add Role</MDButton>
        </DialogActions>
      </Dialog>
      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

AdminAlumniRightsManager.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default AdminAlumniRightsManager; 